/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useId, type ReactElement } from 'react';
import type { PlacedNavGroup } from '@/lib/navigation/columns';
import { SubnavLink } from '@/components/navigation/subnav-link';

/**
 * One grouping category: a static heading over its links. The heading never links —
 * SCO-NAV-04 makes level 2 a static entry grouping level 3, as SCO-NAV-03 C does for 3/4.
 */
export function SubnavGroup({ placed, onNavigate }: { placed: PlacedNavGroup; onNavigate?: () => void }): ReactElement {
    const headingId = useId();
    const { group, columns, span } = placed;

    return (
        <div
            role="group"
            aria-labelledby={headingId}
            className="flex flex-col gap-5"
            style={{ gridColumn: `span ${span}` }}>
            <p id={headingId} className="text-label-xs text-primary uppercase">
                {group.name}
            </p>

            <div className="grid gap-x-10" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
                {columns.map((column) => (
                    // A column is only ever empty when the group itself is, and then there is
                    // exactly one — so the first child's id keys the split unambiguously.
                    <ul key={column[0]?.id ?? group.id} className="flex flex-col gap-3">
                        {column.map((category) => (
                            <li key={category.id}>
                                <SubnavLink category={category} onNavigate={onNavigate} />
                            </li>
                        ))}
                    </ul>
                ))}
            </div>
        </div>
    );
}
